import React, { useEffect, useState, useMemo } from 'react';
import { Table, Card, Button, Space, Typography, Input, Popconfirm, message, Modal, Form, InputNumber, Row, Col } from 'antd';
import { PlusOutlined, SearchOutlined, DeleteOutlined, EyeOutlined, EditOutlined, ReloadOutlined, AppstoreOutlined, DollarOutlined, TrophyOutlined, CalendarOutlined } from '@ant-design/icons';
import StatCard from '../components/StatCard';
import useServiceStore from '../store/serviceStore';

const { Title } = Typography;

const ServicesPage = () => {
  const { services, loading, pagination, fetchServices, deleteService, createService, updateService } = useServiceStore();
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleDelete = async (id) => {
    try {
      await deleteService(id);
      message.success("O'chirildi");
      fetchServices();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingItem) {
        await updateService(editingItem.id, values);
        message.success('Yangilandi');
      } else {
        await createService(values);
        message.success("Qo'shildi");
      }
      setModalOpen(false);
      setEditingItem(null);
      form.resetFields();
      fetchServices();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const openEdit = (record) => {
    setEditingItem(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60, sorter: (a, b) => a.id - b.id },
    { title: 'Nomi', dataIndex: 'title', key: 'title', ellipsis: true },
    {
      title: 'Narxi',
      dataIndex: 'price',
      key: 'price',
      render: (v) => `${Number(v || 0).toLocaleString()} UZS`,
      sorter: (a, b) => a.price - b.price,
    },
    { title: "Sig'im", dataIndex: 'sigim', key: 'sigim' },
    { title: 'Davomiylik', dataIndex: 'davomiy', key: 'davomiy' },
    {
      title: 'Sana',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (d) => d ? new Date(d).toLocaleDateString('uz-UZ') : '-',
    },
    {
      title: 'Amallar',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EyeOutlined />} onClick={() => { setDetailData(record); setDetailOpen(true); }} />
          <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm title="O'chirishni tasdiqlaysizmi?" onConfirm={() => handleDelete(record.id)}>
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filtered = services?.filter((s) =>
    (s.title || '').toLowerCase().includes(search.toLowerCase())
  );

  const stats = useMemo(() => {
    const list = services || [];
    const today = new Date().toISOString().slice(0, 10);
    const prices = list.map(s => Number(s.price || 0));
    return {
      total: list.length,
      avgPrice: list.length > 0 ? Math.round(prices.reduce((a, b) => a + b, 0) / list.length) : 0,
      maxPrice: prices.length > 0 ? Math.max(...prices) : 0,
      today: list.filter(s => (s.created_at || '').slice(0, 10) === today).length,
    };
  }, [services]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Title level={3} style={{ margin: 0 }}>Xizmatlar</Title>
        <Space wrap>
          <Input placeholder="Qidirish..." prefix={<SearchOutlined />} value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 220 }} allowClear />
          <Button icon={<ReloadOutlined />} onClick={() => fetchServices()}>Yangilash</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingItem(null); form.resetFields(); setModalOpen(true); }}>
            Qo'shish
          </Button>
        </Space>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard title="Jami xizmatlar" value={stats.total} icon={<AppstoreOutlined />} color="#4f46e5" loading={loading} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard title="O'rtacha narx" value={stats.avgPrice} suffix="UZS" icon={<DollarOutlined />} color="#13c2c2" loading={loading} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard title="Eng qimmat" value={stats.maxPrice} suffix="UZS" icon={<TrophyOutlined />} color="#fa8c16" loading={loading} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard title="Bugun qo'shilgan" value={stats.today} icon={<CalendarOutlined />} color="#52c41a" loading={loading} />
        </Col>
      </Row>

      <Card style={{ borderRadius: 12 }}>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 800 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Jami: ${total}`,
          }}
          onChange={(pg) => fetchServices({ page: pg.current, per_page: pg.pageSize })}
        />
      </Card>

      <Modal
        title={editingItem ? 'Xizmatni tahrirlash' : "Yangi xizmat qo'shish"}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditingItem(null); form.resetFields(); }}
        onOk={() => form.submit()}
        okText="Saqlash"
        cancelText="Bekor qilish"
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="title" label="Nomi" rules={[{ required: true, message: 'Nomini kiriting' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Tavsif">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="price" label="Narxi (UZS)" rules={[{ required: true, message: 'Narxini kiriting' }]}>
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="sigim" label="Sig'im">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="davomiy" label="Davomiylik (daqiqa)">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="maydon" label="Maydon (m²)">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="stoyan" label="Stoyanka">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Xizmat tafsilotlari" open={detailOpen} onCancel={() => setDetailOpen(false)} footer={null} width={600}>
        {detailData && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div><strong>ID:</strong> {detailData.id}</div>
            <div><strong>Nomi:</strong> {detailData.title}</div>
            <div><strong>Tavsif:</strong> {detailData.description || '-'}</div>
            <div><strong>Narxi:</strong> {Number(detailData.price || 0).toLocaleString()} UZS</div>
            <div><strong>Sig'im:</strong> {detailData.sigim || '-'}</div>
            <div><strong>Davomiylik:</strong> {detailData.davomiy || '-'} daqiqa</div>
            <div><strong>Maydon:</strong> {detailData.maydon || '-'} m²</div>
            <div><strong>Stoyanka:</strong> {detailData.stoyan || '-'}</div>
            <div><strong>Yaratilgan:</strong> {detailData.created_at ? new Date(detailData.created_at).toLocaleString('uz-UZ') : '-'}</div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ServicesPage;
